import React, { useState, useEffect, useRef } from 'react';
import Notification from '../../notification/notification';
import { PlusIcon } from '@heroicons/react/20/solid';
import productApi from '../../../services/productApi';
import itemApi from '../../../services/itemApi';
import tagApi from '../../../services/tagApi';
import storeApi from '../../../services/storeApi';
import ProductUpsertModal from './product-upsert-modal';
import ProductVisibleModal from './components/product-visible-modal';
import ProductCard from './product-card';
import ModalDelete from '../../modal-delete/modal-delete';
import TWConfirmModal from '../../ui/tw-confirm-modal';
import { ProductListSkeleton } from '../../skeleton/skeleton';
import dayjs from 'dayjs';
import { capitalizeWords, capitalizeSentence } from '../../../utils/baseUtils';
import { planPlatformSave } from './platform-visibility';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export default function ProductList({
  selected,
  setSelected,
  onItemCountChange,
  onProductsChanged,
  preferredProductId = null,
  preferredCategoryId = null,
  deepLinkNonce,
  onPreferredConsumed,
}) {
  // Tracks which category.id the currently-loaded productList was fetched
  // for, mirroring CategoryList's lastFetchedMenuIdRef guard (see there for
  // the full rationale).
  const lastFetchedCategoryIdRef = useRef(null);
  const [selectedProductVisible, setSelectedProductVisible] = useState({});
  const [isSingleItem, setIsSingleItem] = useState(true);
  const [deletedItem, setDeletedItem] = useState({ name: '', id: '' });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
    displayorder: false,
    tax: false,
    imagepath: false,
    items: [{ name: false, price: false }],
  });
  const [productForm, setProductForm] = useState({
    id: '',
    name: '',
    description: '',
    categoryid: selected.category.id,
    visible: false,
    isupsell: false,
    usetax: false,
    usecrvtax: false,
    image: '',
    imageid: '',
    imagepath: '',
    libraryimageid: '',
    tax: 0,
    optiongroupids: [],
    items: [{ name: '', price: '', displayorder: 1, showcalories: false }],
    displayorder: '',
    hidedate: '',
    crvtaxrate: 0,
    isdisabled: false,
  });
  const [originalCustomTags, setOriginalCustomTags] = useState([]);
  const [originalPlatformSettings, setOriginalPlatformSettings] = useState([]);
  const [productDialogs, setProductDialogs] = useState({
    productUpsert: false,
    productDelete: false,
    productVisible: false,
    hidedateDelete: false,
  });
  const [productList, setProductList] = useState([]);
  const [storeTimezoneId, setStoreTimezoneId] = useState(null);
  const [loading, setLoading] = useState({
    productLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    visibleLoading: false,
  });

  // Applies the deep-linked preferred product against a fetched list.
  // Called (a) right after every fetch resolves and (b) from the
  // deepLinkNonce effect against the already-fetched list. Guarded on
  // preferredCategoryId: when a category-level deep link is still pending
  // (queued in the same openProductDeepLink call, not yet applied by
  // CategoryList), this list may belong to the wrong category — skip and
  // let the fetch triggered by the eventual category switch call this
  // normally. Consumption fires whenever we actually attempt — matched or
  // not — silent fallback for a stale/bad id.
  const tryApplyPreferred = (list) => {
    if (!preferredProductId) return;
    if (preferredCategoryId && preferredCategoryId !== selected.category?.id) {
      return;
    }
    const match = list.find((p) => p.id === preferredProductId);
    if (match) {
      setSelected((prev) => ({
        ...prev,
        product: match,
        itemid: '',
        optiongroupid: '',
        optionid: '',
      }));
    }
    onPreferredConsumed?.('productid');
  };

  const getProductList = async (method) => {
    try {
      setLoading((prev) => ({ ...prev, productLoading: true }));
      const query = `&categoryid=${selected.category.id}${
        selected.onlyDisabled ? '&isvisible=false' : ''
      }`;

      const list = await productApi.list(query);
      lastFetchedCategoryIdRef.current = selected.category.id;

      if (list.data.data.length > 0) {
        setProductList(list.data.data);
        tryApplyPreferred(list.data.data);
        if (selected.product?.id) {
          const updated = list.data.data.find(
            (p) => p.id === selected.product.id,
          );
          if (updated) {
            setSelected((prev) => ({ ...prev, product: updated }));
          }
        }
      } else {
        setProductList([]);
        tryApplyPreferred([]);
        setSelected((prev) => ({
          ...prev,
          product: { productoptiongroups: [] },
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, productLoading: false }));
    }
  };

  const onClickAddProduct = () => {
    setProductForm({
      id: '',
      name: '',
      description: '',
      categoryid: selected.category.id,
      visible: true,
      isupsell: false,
      image: '',
      imageid: '',
      imagepath: '',
      libraryimageid: '',
      tax: 0,
      optiongroupids: [],
      items: [
        { name: '', price: '', displayorder: 1, calories: '', showcalories: false },
      ],
      displayorder:
        productList.length > 0
          ? productList[productList.length - 1].displayorder + 1
          : 1,
      hidedate: '',
      usecrvtax: false,
      usetax: false,
      crvtaxrate: 0,
      isdisabled: false,
      productsettings: [],
      customproducttags: [],
      squareid: '',
      squarename: '',
      squaremissing: false,
    });

    // Yeni product için original data'ları temizle
    setOriginalCustomTags([]);
    setOriginalPlatformSettings([]);
    setProductDialogs((prev) => ({ ...prev, productUpsert: true }));
  };

  const onFormChange = (val, key) => {
    setProductForm((prev) => ({ ...prev, [key]: val }));

    if (key === 'name' && isSingleItem && !productForm.id) {
      setProductForm((prev) => ({
        ...prev,
        items: [{ ...prev.items[0], name: val }],
      }));
    }
  };

  const onClickDeleteProduct = (product) => {
    setDeletedItem(product);
    setProductDialogs((prev) => ({ ...prev, productDelete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await productApi.deleteById(deletedItem.id);
      getProductList();
      onProductsChanged?.();
    } catch (error) {
    } finally {
      setProductDialogs((prev) => ({
        ...prev,
        productDelete: false,
        productUpsert: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const onClickDuplicateProduct = (product) => {
    setDuplicateTarget(product);
  };

  const runDuplicateProduct = async () => {
    if (!duplicateTarget) return;
    setDuplicateLoading(true);
    try {
      const res = await productApi.duplicate(duplicateTarget.id);
      const newName = res?.data?.data?.name || res?.data?.name || 'product';
      setNotf({
        display: true,
        type: 'success',
        message: `Duplicated as "${newName}"`,
      });
      await getProductList();
      onProductsChanged?.();
    } catch (e) {
      setNotf({
        display: true,
        type: 'error',
        message:
          e?.response?.data?.Message ||
          e?.response?.data ||
          'Duplicate failed',
      });
    } finally {
      setDuplicateLoading(false);
      setDuplicateTarget(null);
    }
  };

  const onClickEditProduct = (val) => {
    setProductForm({ ...val });
    setOriginalCustomTags(val.customproducttags || []);
    setOriginalPlatformSettings(val.productsettings || []);
    setProductDialogs((prev) => ({ ...prev, productUpsert: true }));
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      displayorder: false,
      description: false,
      tax: false,
      imagepath: false,
      name: false,
      items: prev.items.map((item) => ({
        name: false,
        price: false,
      })),
    }));
    // Original data'ları temizle
    setOriginalCustomTags([]);
    setOriginalPlatformSettings([]);
    setProductDialogs((prev) => ({ ...prev, productUpsert: false }));
  };

  const onCloseVisible = () => {
    setErrors((prev) => ({
      ...prev,
      displayorder: false,
      description: false,
      tax: false,
      imagepath: false,
      name: false,
    }));
    setProductDialogs((prev) => ({ ...prev, productVisible: false }));
  };

  const onChangeProductVisible = async (product, val, index) => {
    const hasActiveHideDate =
      product.hidedate && new Date() < new Date(product.hidedate);

    // Eğer aktif bir hidedate varsa, switch'e dokunmak sadece hidedate'i kaldırmalı
    if (hasActiveHideDate) {
      const updatedProduct = { ...product, hidedate: '' };
      // Ürünü tekrar hemen görünür yap (visible = true)
      await onChangeSubmitProductVisible(updatedProduct, true, index);
      return;
    }

    if (!val) {
      // Normal durumda OFF'a çekmek → ne kadar süre gizleneceğini seçmek için modal
      onClickOpenVisibleModal(product, val, index);
    } else {
      // Normal durumda ON'a çekmek → sadece visible flag'ini güncelle
      await onChangeSubmitProductVisible(product, val, index);
    }
  };

  const onClickOpenVisibleModal = (product, val, index) => {
    setSelectedProductVisible({ product, val, index });

    setProductDialogs((prev) => ({ ...prev, productVisible: true }));
  };

  const onSelectVisible = async (time) => {
    let { product, val, index } = selectedProductVisible;

    val = true;

    // Store timezone kullan. Backend'e timezone offset'li gönder (örn. -08:00).
    const tz = storeTimezoneId || 'UTC';
    const base = dayjs().tz(tz);

    let hidedate;

    if (time === 'tomorrow') {
      hidedate = base
        .add(1, 'day')
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ssZ');
      product.hidedate = hidedate;
    } else if (time === 'forever') {
      val = false;
      product.hidedate = '';
    } else {
      hidedate = base.add(Number(time), 'hour').format('YYYY-MM-DDTHH:mm:ssZ');
      product.hidedate = hidedate;
    }
    await onChangeSubmitProductVisible(product, val, index);

    setProductDialogs((prev) => ({ ...prev, productVisible: false }));

    setSelectedProductVisible({});
  };

  const onChangeSubmitProductVisible = async (product, val, index) => {
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));

      const formData = new FormData();

      for (const key of Object.keys(product)) {
        if (
          (key === 'description' || key === 'hidedate') &&
          (product[key] === '' || product[key] === null)
        ) {
          formData.append(key, '');
        } else if (key === 'visible') {
          formData.append(key, val);
        } else if (key === 'libraryimageid') {
          // Nullable FK — never send the literal "null" (violates
          // fk_products_libraryimage). Empty string = no library link.
          const v = product[key];
          formData.append('libraryimageid', v && v !== 'null' ? v : '');
        } else {
          // Coerce null/undefined to '' so they aren't sent as the string "null".
          const v = product[key];
          formData.append(key, v === null || v === undefined ? '' : v);
        }
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };
      setProductList((prev) => {
        const newList = [...prev];
        newList[index].visible = val;
        return newList;
      });

      await productApi.upsert(formData, config);
      onProductsChanged?.();
    } catch (error) {
    } finally {
      await getProductList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
    }
  };

  const onFormMultipleItemSubmit = async (productid) => {
    try {
      for (const [index, item] of productForm.items.entries()) {
        const body = {
          name: item.name,
          productid,
          price: item.price,
          displayorder: index + 1,
          calories: item.calories,
          showcalories: item.showcalories !== false,
        };
        await itemApi.upsert(body);
      }
      setErrors((prev) => ({
        ...prev,
        name: false,
        price: false,
      }));
      setIsSingleItem(true);
    } catch (error) {}
  };

  const onFormSingleItemSubmit = async (productid) => {
    try {
      // Capitalize product name before saving
      let capitalizedName = productForm.name.trim();
      if (capitalizedName) {
        capitalizedName = capitalizeWords(capitalizedName);
      }
      const body = {
        ...productForm.items[0],
        productid,
        name: capitalizedName,
      };
      await itemApi.upsert(body);
    } catch (error) {
    } finally {
    }
  };

  const validateForm = () => {
    if (!productForm.name) {
      setErrors((prev) => ({ ...prev, name: true }));
      return false;
    }
    if (!productForm.id) {
      const errorItems = productForm.items.map((item) => ({
        name: !item.name,
        price: item.price === '',
      }));

      setErrors((prev) => ({ ...prev, items: errorItems }));
      return !errorItems.some((error) => error.name || error.price);
    }

    if (productForm.tax < 0 || productForm.tax > 100) {
      setErrors((prev) => ({ ...prev, tax: true }));
      return false;
    }

    return true;
  };

  const handleFormData = async () => {
    const formData = new FormData();
    for (const key of Object.keys(productForm)) {
      if (key === 'image' && productForm['image'] !== '') {
        if (productForm.imageid) {
          await productApi.deleteImageById(productForm.id);
        }
        formData.append('image', productForm.image);
      } else if (key === 'description' || key === 'hidedate') {
        let value = productForm[key] ? productForm[key] : '';
        // Capitalize description by sentence
        if (key === 'description' && value) {
          value = capitalizeSentence(value);
        }
        formData.append(key, value);
      } else if (key === 'name') {
        // Capitalize product name before saving
        let capitalizedName = productForm[key].trim();
        if (capitalizedName) {
          capitalizedName = capitalizeWords(capitalizedName);
        }
        formData.append(key, capitalizedName);
      } else if (key === 'libraryimageid') {
        // Nullable FK. FormData stringifies JS null to the literal "null",
        // which violates fk_products_libraryimage. Send empty string when
        // there is no library link (backend treats "" as unlink, doc §4.1).
        const v = productForm[key];
        formData.append('libraryimageid', v && v !== 'null' ? v : '');
      } else {
        // Coerce null/undefined to '' so they aren't sent as the string "null".
        const v = productForm[key];
        formData.append(key, v === null || v === undefined ? '' : v);
      }
    }
    return formData;
  };

  // Returns false when the platform visibility could not be saved; the
  // product itself is already saved by then.
  const savePlatformTags = async (productId) => {
    try {
      const plan = planPlatformSave(
        originalPlatformSettings,
        productForm.productsettings,
        productId
      );
      if (plan.update) await tagApi.updatePlatformTag(plan.update);
      if (plan.create) await tagApi.createPlatformTag(plan.create);
      // Despite its name, this endpoint deletes one row by the row's id.
      for (const rowId of plan.deleteIds) {
        await tagApi.deletePlatformTagByProductId(rowId);
      }
      return true;
    } catch (error) {
      console.error('Error saving platform tags:', error);
      return false;
    }
  };

  const saveCustomTags = async (productId) => {
    console.log('Original custom tags:', originalCustomTags);
    console.log('Current custom tags:', productForm.customproducttags);

    try {
      const currentTag = productForm.customproducttags?.[0];
      const originalTag = originalCustomTags?.[0];

      if (!currentTag || (!currentTag.tagid && !currentTag.tag?.id)) {
        if (originalTag && originalTag.id) {
          await tagApi.deleteCustomProductTag(originalTag.id);
        }
        return;
      }

      if (originalTag && originalTag.id) {
        const customTagData = {
          id: originalTag.id,
          tagid: currentTag.tagid || currentTag.tag?.id,
        };
        await tagApi.updateCustomProductTag(customTagData);
      } else {
        const customTagData = {
          productId,
          tagid: currentTag.tagid || currentTag.tag?.id,
        };
        await tagApi.createCustomProductTag(customTagData);
      }
    } catch (error) {
      console.error('Error saving custom tags:', error);
    }
  };

  const onSubmitSuccess = async (productid) => {
    const platformSaved = await savePlatformTags(productid);
    const message = !platformSaved
      ? 'Product saved, but its platform visibility could not be saved. Try again.'
      : productForm.id
        ? 'Product has been successfully updated.'
        : 'Product has been successfully created.';
    setNotf((prev) => ({
      ...prev,
      message,
      display: true,
      type: platformSaved ? 'success' : 'error',
    }));

    await saveCustomTags(productid);

    // Items only get seeded from the upsert flow when CREATING a product.
    // For existing products, items are managed via the standalone ItemList on
    // the menu page — re-sending productForm.items here would clobber any edits
    // made through that flow with the stale snapshot taken when the modal opened.
    if (!productForm.id) {
      const submitAction = isSingleItem
        ? onFormSingleItemSubmit
        : onFormMultipleItemSubmit;
      return submitAction(productid);
    }
  };

  const onFormSubmit = async () => {
    if (!validateForm()) return;
    setLoading((prev) => ({ ...prev, upsertLoading: true }));

    try {
      const formData = await handleFormData();
      const res = await productApi.upsert(formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.data) await onSubmitSuccess(res.data.data.id);
      onProductsChanged?.();
    } catch (error) {
      const message =
        error.response?.data === 'Upsell count max 4'
          ? 'You have already selected 4 add-ons. Delete one to add more.'
          : error.response?.data || error.message;
      setNotf((prev) => ({ ...prev, message, display: true }));
    } finally {
      setErrors((prev) => ({
        ...prev,
        name: false,
        price: false,
        tax: false,
      }));

      setProductDialogs((prev) => ({ ...prev, productUpsert: false }));
      await getProductList({ method: productForm.id ? 'update' : 'create' });
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = productList.findIndex((item) => item.id === active.id);
    const newIndex = productList.findIndex((item) => item.id === over.id);
    const reordered = arrayMove(productList, oldIndex, newIndex);

    setProductList(reordered);

    try {
      await productApi.reorder({
        items: reordered.map((item, index) => ({
          id: item.id,
          displayorder: index,
        })),
      });
    } catch (error) {
      console.error('Error reordering products:', error);
      await getProductList();
    }
  };

  useEffect(() => {
    getProductList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.category.id, selected.onlyDisabled]);

  // Re-attempt a queued in-page deep link (needs-review modal) against the
  // list we already have. Guarded on lastFetchedCategoryIdRef so a nonce
  // bump that arrives before a pending category switch (from a cross-category
  // deep link) lands doesn't apply against the previous category's stale
  // list — the effect above's post-fetch call handles that case once the new
  // list lands (see tryApplyPreferred's own preferredCategoryId guard too).
  useEffect(() => {
    if (lastFetchedCategoryIdRef.current !== selected.category.id) return;
    tryApplyPreferred(productList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkNonce]);

  // Menu'ya bağlı ilk store'un timezone'unu al (hidedate hesaplaması için)
  const menuStoreIds = selected.menuStoreIds;
  useEffect(() => {
    const firstStoreId = menuStoreIds?.[0];
    if (!firstStoreId) {
      setStoreTimezoneId(null);
      return;
    }
    let cancelled = false;
    storeApi
      .getById(firstStoreId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data ?? res.data;
        setStoreTimezoneId(data?.timezoneid || null);
      })
      .catch(() => {
        if (!cancelled) setStoreTimezoneId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [menuStoreIds]);

  return (
    <>
      {loading.productLoading ? (
        <ProductListSkeleton />
      ) : (
        <nav className="">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={productList.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {productList.map((product, i) => (
                <ProductCard
                  key={product.id}
                  index={i}
                  id={product.id}
                  selected={selected}
                  setSelected={setSelected}
                  onClickEditProduct={onClickEditProduct}
                  onClickDuplicateProduct={onClickDuplicateProduct}
                  onChangeProductVisible={onChangeProductVisible}
                  product={product}
                  storeTimezoneId={storeTimezoneId}
                />
              ))}
            </SortableContext>
          </DndContext>
          <div
            onClick={() => onClickAddProduct()}
            className="mx-3 my-2 flex min-h-[5.75rem] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-blue-300 bg-blue-50/40 p-4 text-sm font-medium text-blue-600 transition hover:bg-blue-50 dark:border-slate-500 dark:bg-slate-700/40 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            <button
              type="button"
              className="rounded-full bg-blue-600 p-1 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <span className="mt-1 truncate font-semibold text-[0.925rem]">
              Add Product
            </span>
          </div>
        </nav>
      )}

      <ProductUpsertModal
        show={productDialogs.productUpsert}
        loading={loading.upsertLoading}
        selectedCategory={selected.category}
        productForm={productForm}
        setProductForm={setProductForm}
        setIsSingleItem={setIsSingleItem}
        isSingleItem={isSingleItem}
        errors={errors}
        onClickDeleteProduct={onClickDeleteProduct}
        onCloseUpsert={onCloseUpsert}
        onFormChange={onFormChange}
        onFormSubmit={onFormSubmit}
      />
      <ProductVisibleModal
        onSelectVisible={onSelectVisible}
        show={productDialogs.productVisible}
        loading={loading.visibleLoading}
        productForm={productForm}
        errors={errors}
        onCloseVisible={onCloseVisible}
      />

      <ModalDelete
        title="Product"
        text={`Are you sure you want to delete "${deletedItem.name}"? All associated data will be permanently removed.${
          deletedItem.squareid
            ? ' This product is linked to Square — the next sync will re-add it as hidden. Consider hiding it instead.'
            : ''
        }`}
        loading={loading.deleteLoading}
        open={productDialogs.productDelete}
        onCloseRequest={() =>
          setProductDialogs((prev) => ({ ...prev, productDelete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedItem}
      />
      <TWConfirmModal
        open={!!duplicateTarget}
        tone="info"
        title={`Duplicate "${duplicateTarget?.name || ''}"?`}
        description={
          duplicateTarget
            ? 'A copy will be created and appended to the end of this category. Items and modifier links will be copied. Modifier group definitions are shared (not duplicated).'
            : ''
        }
        confirmLabel="Duplicate"
        loading={duplicateLoading}
        onConfirm={runDuplicateProduct}
        onClose={() => !duplicateLoading && setDuplicateTarget(null)}
      />
      <Notification
        classAttributes="pt-20"
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </>
  );
}
